import { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { serviceGroupsApi, type ServiceGroupDto } from "@/api/endpoints";

interface Props {
  value: ServiceGroupDto | null;
  onChange: (v: ServiceGroupDto | null) => void;
  /** When the parent holds only an id (editing an existing row), the picker resolves it. */
  initialId?: string | null;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

function useDebouncedValue(value: string, delay = 250) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setD(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return d;
}

/**
 * Lookup over active service groups. Groups are a small master, so one page of 100 covers the
 * list and the initialId resolution without a second round trip.
 */
export function ServiceGroupPicker({
  value, onChange, initialId, label = "Service group",
  required, error, helperText, disabled,
}: Props) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input);

  const q = useQuery({
    queryKey: ["service-groups-picker", debounced],
    queryFn: () => serviceGroupsApi.list({ search: debounced || undefined, isActive: true, page: 1, pageSize: 100 }),
    staleTime: 5 * 60_000,
  });

  const resolved = !value && initialId && q.data
    ? q.data.items.find(g => g.id === initialId) ?? null
    : value;

  return (
    <Autocomplete<ServiceGroupDto>
      value={resolved}
      options={q.data?.items ?? []}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.code} — ${o.name}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params} label={`${label}${required ? " *" : ""}`}
          error={error} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}
